import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  LinearProgress,
  Modal,
  Snackbar,
  Typography,
} from '@mui/material';

// 作成したカスタムフックとコンポーネントをインポート
import { useDashboardData } from '../../hooks/useDashboardData';
import { useDownloadDashboard } from '../../hooks/useDownloadDashboard';
import { DashboardHeader } from './DashboardHeader';
import DashboardFilters from './DashboardFilters';
import TabPanel from './TabPanel'; // このインポートはそのままにします
import OverviewTab, {
  getDepartmentCapturePageCount,
} from './OverviewTab';
import ComplianceRateTab from './ComplianceRateTab';
import DetailedAnalysisTab from './DetailedAnalysisTab';
import TimeSeriesTab, { TimeSeriesTabHandle } from './TimeSeriesTab';
import ReasonTab, { ReasonTabHandle } from './ReasonTab';
import {
  ChartCaptureSizeError,
  domToImageBlob,
} from './utils/chartDownloader';
import { ReasonAggregationGrouping } from './utils/dataProcessing';
import {
  TIME_SERIES_IMAGE_GRANULARITIES,
  getTimeSeriesCaptureStateKey,
  getTimeSeriesImageMetrics,
} from './utils/timeSeriesImageTargets';
import { getUserFacingApiErrorMessage } from '../../utils/apiErrorMessage';

type ChartImageFile = { name: string; blob: Blob };

type CaptureProgress = {
  completed: number;
  total: number;
  label: string;
};

const CHART_RENDER_TIMEOUT_MS = 5000;
const CHART_RENDER_POLL_MS = 100;
const MAX_DASHBOARD_CHART_IMAGES = 32;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });

const waitForChartUpdate = async () => {
  await waitForNextPaint();
};

type ChartReadinessCheck = (element: HTMLElement) => boolean;

const isRenderableChartElement = (
  element: Element | null,
  isReady: ChartReadinessCheck = () => true,
): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  const hasSize =
    (rect.width > 0 && rect.height > 0) ||
    (element.offsetWidth > 0 && element.offsetHeight > 0);
  const hasSvg = element.querySelector('svg') !== null;

  return hasSize && hasSvg && isReady(element);
};

const waitForChartElement = async (
  getElement: () => Element | null,
  timeoutMs = CHART_RENDER_TIMEOUT_MS,
  isReady: ChartReadinessCheck = () => true,
): Promise<HTMLElement | null> => {
  const startedAt = Date.now();

  while (Date.now() - startedAt <= timeoutMs) {
    await waitForNextPaint();

    const element = getElement();
    if (isRenderableChartElement(element, isReady)) {
      return element;
    }

    await wait(CHART_RENDER_POLL_MS);
  }

  return null;
};

const hasCaptureState =
  (expectedState: string): ChartReadinessCheck =>
  (element) =>
    element.dataset.dashboardCaptureState === expectedState;

export const createStableOverviewLegendCheck = (): ChartReadinessCheck => {
  let previousLayout: string | null = null;

  return (element) => {
    const legends = Array.from(
      element.querySelectorAll('[data-dashboard-export-legend="true"]'),
    );
    if (
      legends.length === 0 ||
      legends.some(
        (legend) =>
          !(legend instanceof HTMLElement) ||
          legend.scrollHeight > legend.clientHeight + 1,
      )
    ) {
      previousLayout = null;
      return false;
    }

    const chartRect = element.getBoundingClientRect();
    const currentLayout = [
      chartRect.width,
      chartRect.height,
      ...legends.flatMap((legend) => {
        const legendElement = legend as HTMLElement;
        const legendRect = legendElement.getBoundingClientRect();
        return [
          legendRect.width,
          legendRect.height,
          legendElement.clientHeight,
          legendElement.scrollHeight,
        ];
      }),
    ].join(':');
    const isStable = currentLayout === previousLayout;
    previousLayout = currentLayout;
    return isStable;
  };
};

const Dashboard: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();

  const overviewTabRef = useRef<HTMLDivElement>(null);
  const complianceRateTabRef = useRef<HTMLDivElement>(null);
  const reasonTabRef = useRef<HTMLDivElement>(null);
  const reasonTabHandleRef = useRef<ReasonTabHandle>(null);
  const timeSeriesTabContainerRef = useRef<HTMLDivElement>(null);
  const timeSeriesTabRef = useRef<TimeSeriesTabHandle>(null);
  const [captureProgress, setCaptureProgress] = useState<CaptureProgress | null>(null);
  const [isChartCaptureMode, setIsChartCaptureMode] = useState(false);

  // データ取得とタブ状態管理をカスタムフックに集約
  const {
    dashboardData,
    filteredDashboardData,
    filterOptions,
    filters,
    isLoading,
    isFetching,
    error,
    refetch,
    activeTab,
    hasReasonData,
    hasTimeSeriesData,
    handleTabChange,
    setActiveTab,
    handleFiltersChange,
    resetFilters,
    fileName,
  } = useDashboardData(fileId);

  // getChartImages関数：chartDownloaderのBlob変換関数を使う
  const getChartImages = async () => {
    const chartFiles: ChartImageFile[] = [];
    const originalTab = activeTab;
    const reasonGroupingLabels: Record<ReasonAggregationGrouping, string> = {
      department: '部署別',
      occupation: '職種別',
      timing: '観察タイミング別',
      departmentOccupation: '部署×職種別',
      departmentTiming: '部署×観察タイミング別',
      departmentOccupationTiming: '部署×職種×観察タイミング別',
    };
    const timeSeriesGranularityLabels = {
      month: '月次',
      quarter: '四半期',
      year: '年次',
      fiscalYear: '年度',
    } as const;
    const overviewChartNames = ['概要_職種', '概要_観察タイミング', '概要_結果'];
    const departmentCapturePageCount =
      getDepartmentCapturePageCount(filteredDashboardData);
    const complianceChartNames = ['遵守率_部署', '遵守率_職種', '遵守率_観察タイミング'];
    const reasonGroupings: ReasonAggregationGrouping[] = [
      'department',
      'occupation',
      'timing',
    ];

    const timeSeriesMetricLabels = getTimeSeriesImageMetrics(hasReasonData);
    const totalCaptureSteps =
      overviewChartNames.length +
      departmentCapturePageCount +
      complianceChartNames.length +
      (hasTimeSeriesData
        ? timeSeriesMetricLabels.length * TIME_SERIES_IMAGE_GRANULARITIES.length
        : 0) +
      (hasReasonData ? 1 + reasonGroupings.length : 0);

    const originalTimeSeriesState = timeSeriesTabRef.current?.getCurrentState();
    const originalReasonGrouping = reasonTabHandleRef.current?.getCurrentGrouping();

    const setProgressLabel = (label: string) => {
      setCaptureProgress((prev) => (prev ? { ...prev, label } : prev));
    };

    const completeProgressStep = (label: string) => {
      setCaptureProgress((prev) =>
        prev
          ? {
              ...prev,
              completed: Math.min(prev.completed + 1, prev.total),
              label,
            }
          : prev,
      );
    };

    const captureChart = async (
      baseName: string,
      getElement: () => Element | null,
      timeoutMs = CHART_RENDER_TIMEOUT_MS,
      isReady: ChartReadinessCheck = () => true,
    ) => {
      setProgressLabel(`${baseName}を取得中です。`);

      try {
        const chart = await waitForChartElement(getElement, timeoutMs, isReady);
        if (!chart) {
          const candidate = getElement();
          const currentState = candidate instanceof HTMLElement
            ? candidate.dataset.dashboardCaptureState ?? '状態キーなし'
            : '要素なし';
          throw new Error(
            `レポート出力時にチャートを取得できませんでした: ${baseName} (${currentState})`,
          );
        }

        const blob = await domToImageBlob(chart);
        chartFiles.push({ name: `${baseName}.png`, blob });
      } finally {
        completeProgressStep(`${baseName}を処理しました。`);
      }
    };

    const getIndexedChart = (
      container: HTMLDivElement | null,
      selector: string,
      index: number,
    ) => container?.querySelectorAll(selector).item(index) ?? null;

    if (totalCaptureSteps > MAX_DASHBOARD_CHART_IMAGES) {
      throw new ChartCaptureSizeError(
        '部署数が多いため、グラフ画像数が出力上限を超えます。',
      );
    }

    flushSync(() => {
      setCaptureProgress({
        completed: 0,
        total: totalCaptureSteps,
        label: 'グラフを準備しています。',
      });
      setIsChartCaptureMode(true);
    });

    try {
      await waitForChartUpdate();
      // 概要タブのグラフを取得
      flushSync(() => setActiveTab('overview'));
      await waitForChartUpdate();
      for (let i = 0; i < overviewChartNames.length; i++) {
        await captureChart(
          overviewChartNames[i],
          () => getIndexedChart(overviewTabRef.current, '.recharts-wrapper', i),
          CHART_RENDER_TIMEOUT_MS,
          createStableOverviewLegendCheck(),
        );
      }
      for (let pageIndex = 0; pageIndex < departmentCapturePageCount; pageIndex++) {
        const pageName =
          departmentCapturePageCount === 1
            ? '概要_部署'
            : '概要_部署_' + (pageIndex + 1);
        await captureChart(
          pageName,
          () =>
            getIndexedChart(
              overviewTabRef.current,
              '.department-chart-capture-target .recharts-wrapper',
              pageIndex,
            ),
        );
      }

      // 遵守率タブのグラフを取得
      flushSync(() => setActiveTab('compliance'));
      await waitForChartUpdate();
      for (let i = 0; i < complianceChartNames.length; i++) {
        await captureChart(
          complianceChartNames[i],
          () => getIndexedChart(complianceRateTabRef.current, '.recharts-wrapper', i),
          CHART_RENDER_TIMEOUT_MS,
        );
      }

      // 時系列タブのグラフを取得
      if (hasTimeSeriesData) {
        flushSync(() => setActiveTab('timeseries'));
        await waitForChartUpdate();

        const timeSeriesContainer = timeSeriesTabContainerRef.current;
        const timeSeriesHandle = timeSeriesTabRef.current;
        if (!timeSeriesContainer || !timeSeriesHandle) {
          throw new Error('時系列チャートの出力準備が完了しませんでした');
        }

        for (const metric of timeSeriesMetricLabels) {
          for (const granularity of TIME_SERIES_IMAGE_GRANULARITIES) {
            flushSync(() => {
              timeSeriesHandle.setMetric(metric);
              timeSeriesHandle.setDisplayMode('ratio');
              timeSeriesHandle.setGranularity(granularity);
            });
            const expectedState = getTimeSeriesCaptureStateKey(
              metric,
              granularity,
              'ratio',
            );
            await waitForChartUpdate();

            await captureChart(
              `時系列_${metric}_${timeSeriesGranularityLabels[granularity]}`,
              () =>
                timeSeriesContainer.querySelector(
                  '.timeseries-chart-capture-target',
                ),
              CHART_RENDER_TIMEOUT_MS,
              hasCaptureState(expectedState),
            );
          }
        }
      }

      // 未実施理由タブのグラフを取得
      if (hasReasonData) {
        flushSync(() => setActiveTab('reason'));
        await waitForChartUpdate();

        const reasonContainer = reasonTabRef.current;
        const reasonHandle = reasonTabHandleRef.current;
        if (!reasonContainer || !reasonHandle) {
          throw new Error('未実施理由チャートの出力準備が完了しませんでした');
        }

        await captureChart(
          '未実施理由_理由一覧',
          () => reasonContainer.querySelector('.reason-summary-chart'),
        );

        const stackedChartElement = () =>
          reasonContainer.querySelector('.reason-stacked-chart-container');

        for (const grouping of reasonGroupings) {
          flushSync(() => reasonHandle.setGrouping(grouping));
          await waitForChartUpdate();
          await captureChart(
            `未実施理由_${reasonGroupingLabels[grouping]}`,
            stackedChartElement,
            CHART_RENDER_TIMEOUT_MS,
            hasCaptureState(grouping),
          );
        }
      }

      if (chartFiles.length !== totalCaptureSteps) {
        throw new Error(
          `必要なチャート画像をすべて取得できませんでした: ${chartFiles.length}/${totalCaptureSteps}`,
        );
      }
    } finally {
      flushSync(() => {
        setActiveTab(originalTab);
        setIsChartCaptureMode(false);
      });
      await waitForChartUpdate();

      flushSync(() => {
        if (originalTimeSeriesState && timeSeriesTabRef.current) {
          timeSeriesTabRef.current.setGranularity(originalTimeSeriesState.granularity);
          timeSeriesTabRef.current.setMetric(originalTimeSeriesState.metric);
          timeSeriesTabRef.current.setDisplayMode(originalTimeSeriesState.displayMode);
        }
        if (originalReasonGrouping && reasonTabHandleRef.current) {
          reasonTabHandleRef.current.setGrouping(originalReasonGrouping);
        }
      });
      await waitForChartUpdate();
      setCaptureProgress(null);
    }
    return chartFiles;
  };

  // ダウンロード処理をカスタムフックに集約
  const { isDownloading, downloadError, handleDownload, clearDownloadError } =
    useDownloadDashboard(
    fileId,
    filteredDashboardData,
    getChartImages,
    {
      includeReasonSheets: hasReasonData,
      timeSeriesValueMode: 'ratioAndCount',
      filters,
    },
    fileName,
  );

  const handleDownloadErrorClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    clearDownloadError();
  };

  const guardedHandleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      if (isDownloading) {
        return;
      }

      handleTabChange(event, newValue);
    },
    [handleTabChange, isDownloading],
  );

  const progressPercent = captureProgress && captureProgress.total > 0
    ? (captureProgress.completed / captureProgress.total) * 100
    : 0;

  // 以降のreturn文は変更なし ...
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dashboardData || !filteredDashboardData) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()} disabled={isFetching}>
            再試行
          </Button>
        }
      >
        {getUserFacingApiErrorMessage(error)}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <DashboardHeader
        fileName={fileName || ''}
        activeTab={activeTab}
        hasReasonData={hasReasonData}
        hasTimeSeriesData={hasTimeSeriesData}
        onTabChange={guardedHandleTabChange}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />

      <Modal
        open={isDownloading}
        aria-labelledby="dashboard-download-progress-title"
        aria-describedby="dashboard-download-progress-description"
      >
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: '100%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            outline: 'none',
          }}
        >
          <Typography id="dashboard-download-progress-title" variant="h6" sx={{ mb: 1 }}>
            データ出力中
          </Typography>
          <Typography
            id="dashboard-download-progress-description"
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {captureProgress?.label ?? 'ZIPファイルを作成しています。'}
          </Typography>
          <LinearProgress
            variant={captureProgress ? 'determinate' : 'indeterminate'}
            value={progressPercent}
          />
          {captureProgress && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              {captureProgress.completed} / {captureProgress.total}枚
            </Typography>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={isDownloading}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          データ出力中です。しばらくお待ちください。
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(downloadError)}
        autoHideDuration={6000}
        onClose={handleDownloadErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleDownloadErrorClose}
          severity="error"
          closeText="閉じる"
          sx={{ width: '100%' }}
        >
          {downloadError}
        </Alert>
      </Snackbar>

      <DashboardFilters
        filters={filters}
        options={filterOptions}
        onFiltersChange={handleFiltersChange}
        onReset={resetFilters}
      />

      <div ref={overviewTabRef}>
        <TabPanel value={activeTab} index="overview">
          <OverviewTab
            data={filteredDashboardData}
            isChartCaptureMode={isChartCaptureMode}
          />
        </TabPanel>
      </div>
      <div ref={complianceRateTabRef}>
        <TabPanel value={activeTab} index="compliance">
          <ComplianceRateTab data={filteredDashboardData} />
        </TabPanel>
      </div>
      {hasReasonData && (
        <div ref={reasonTabRef}>
          <TabPanel value={activeTab} index="reason">
            <ReasonTab
              ref={reasonTabHandleRef}
              data={filteredDashboardData}
              hasReasonData={hasReasonData}
              isChartCaptureMode={isChartCaptureMode}
            />
          </TabPanel>
        </div>
      )}
      <div>
        <TabPanel value={activeTab} index="analysis">
          <DetailedAnalysisTab data={filteredDashboardData} />
        </TabPanel>
      </div>
      {hasTimeSeriesData && (
        <div ref={timeSeriesTabContainerRef}>
          <TabPanel value={activeTab} index="timeseries">
            {/* DashboardFiltersは全タブ共通の絞り込みを担い，時系列専用条件はTimeSeriesTab内で独立して扱う． */}
            <TimeSeriesTab
              ref={timeSeriesTabRef}
              data={filteredDashboardData}
              hasReasonData={hasReasonData}
              filters={filters}
            />
          </TabPanel>
        </div>
      )}
    </Container>
  );
};

export default Dashboard;
